import React, { useState, useEffect } from 'react';
import { X, Check, Calendar, Building, Filter } from 'lucide-react';
import { getListPaymentsByBuildingId } from '../../Fetch/fetchPayment';
import { getListApartmensByBuildingId } from '../../Fetch/fetchapartment';
import { getListUsers } from '../../Fetch/fetchUser';
import styles from './PaymentStatusOverview.module.css';

const PaymentStatusOverview = ({ onClose, buildingId }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedApartment, setSelectedApartment] = useState('all');
  const [payments, setPayments] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentMatrix, setPaymentMatrix] = useState({});

  const months = [
    { num: 1, name: 'ינואר' },
    { num: 2, name: 'פברואר' },
    { num: 3, name: 'מרץ' },
    { num: 4, name: 'אפריל' },
    { num: 5, name: 'מאי' },
    { num: 6, name: 'יוני' },
    { num: 7, name: 'יולי' },
    { num: 8, name: 'אוגוסט' },
    { num: 9, name: 'ספטמבר' },
    { num: 10, name: 'אוקטובר' },
    { num: 11, name: 'נובמבר' },
    { num: 12, name: 'דצמבר' }
  ];

  // Generate year options (current year +/- 5 years)
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // JavaScript months are 0-based
  const yearOptions = [];
  for (let year = currentYear - 5; year <= currentYear + 5; year++) {
    yearOptions.push(year);
  }

  useEffect(() => {
    // Reset state when component opens
    setPaymentMatrix({});
    setApartments([]);
    setPayments([]);
    fetchData();
  }, [buildingId, selectedYear]);

  const fetchData = async () => {
    console.log('=== FETCHDATA CALLED ===', 'buildingId:', buildingId, 'selectedYear:', selectedYear);
    setLoading(true);
    try {
      // Try to fetch apartments first
      console.log('Fetching apartments for building:', buildingId);
      let apartmentList = [];
      
      try {
        console.log('BEFORE apartment API call');
        const apartmentData = await getListApartmensByBuildingId(buildingId);
        console.log('AFTER apartment API call - Raw apartment data:', apartmentData);
        
        // Check if apartmentData is nested
        let apartments = apartmentData;
        if (apartmentData && apartmentData.data) {
          apartments = apartmentData.data;
        } else if (apartmentData && apartmentData.result) {
          apartments = apartmentData.result;
        }
        
        if (apartments && apartments.length > 0) {
          apartmentList = apartments;
          console.log('SUCCESS: Found apartments directly:', apartmentList.length);
        } else {
          console.log('No apartments found in direct fetch, apartments:', apartments);
        }
      } catch (aptError) {
        console.log('ERROR: Could not fetch apartments, trying users instead:', aptError);
      }
      
      // If no apartments, try to get from users
      if (apartmentList.length === 0) {
        console.log('No apartments found, fetching users...');
        const userData = await getListUsers(buildingId);
        console.log('Raw user data:', userData);
        
        // Extract unique apartments from users
        const apartmentMap = {};
        if (userData && Array.isArray(userData)) {
          console.log('Sample user structure:', userData[0]);
          userData.forEach(user => {
            console.log('Processing user:', user);
            // Try multiple property names for apartment number
            const aptNum = user.apartmentNumber || 
                          user.ApartmentNumber || 
                          user.apartment_number ||
                          user.Apartment ||
                          user.apartment ||
                          user.ApartmentNum ||
                          user.apt ||
                          user.Apt;
            
            const aptId = user.apartmentId || 
                         user.ApartmentID || 
                         user.apartmentID ||
                         user.apartment_id ||
                         user.id ||
                         user.userId ||
                         user.UserID;
            
            console.log(`User apartment info: aptNum=${aptNum}, aptId=${aptId}`);
            
            if (aptNum && !apartmentMap[aptNum]) {
              apartmentMap[aptNum] = {
                apartmentNumber: aptNum,
                apartmentID: aptId || aptNum,
                tenantName: user.fullName || user.FullName || user.name || user.Name
              };
            }
          });
          apartmentList = Object.values(apartmentMap);
          console.log('Extracted apartments from users:', apartmentList);
        }
      }
      
      console.log('Final apartment list:', apartmentList);
      
      // Sort apartments by number
      const sortedApartments = (apartmentList || []).sort((a, b) => {
        const numA = parseInt(a.apartmentNumber || a.ApartmentNumber || 0);
        const numB = parseInt(b.apartmentNumber || b.ApartmentNumber || 0);
        return numA - numB;
      });
      console.log('Sorted apartments:', sortedApartments);
      setApartments(sortedApartments);

      // Fetch all payments
      console.log('Fetching payments for building:', buildingId);
      const paymentData = await getListPaymentsByBuildingId(buildingId);
      console.log('Raw payment data:', paymentData);
      
      // Check if paymentData is nested
      let payments = paymentData;
      if (paymentData && paymentData.data) {
        payments = paymentData.data;
      } else if (paymentData && paymentData.result) {
        payments = paymentData.result;
      }
      
      console.log('Processed payments:', payments);
      setPayments(payments || []);

      // Extract apartments from payments if no apartments were found
      if (!sortedApartments || sortedApartments.length === 0) {
        const apartmentSet = new Set();
        (payments || []).forEach(payment => {
          const aptNum = payment.apartmentNumber || payment.ApartmentNumber;
          if (aptNum) {
            apartmentSet.add(aptNum);
          }
        });
        
        const extractedApartments = Array.from(apartmentSet)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map(aptNum => ({
            apartmentNumber: aptNum,
            apartmentID: aptNum
          }));
        
        console.log('Extracted apartments from payments:', extractedApartments);
        setApartments(extractedApartments);
        processPaymentMatrix(extractedApartments, payments || []);
      } else {
        processPaymentMatrix(sortedApartments, payments || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('שגיאה בטעינת נתונים: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const processPaymentMatrix = (apartmentList, paymentList) => {
    const matrix = {};

    // Extract all unique apartment numbers from both apartments and payments
    const apartmentSet = new Set();
    
    // Add apartments from apartment list
    apartmentList.forEach(apt => {
      const aptNum = apt.apartmentNumber || apt.ApartmentNumber || apt.number;
      if (aptNum) {
        apartmentSet.add(String(aptNum));
      }
    });
    
    // Add apartments from payments
    paymentList.forEach(payment => {
      const aptNum = payment.apartmentNumber || payment.ApartmentNumber;
      if (aptNum) {
        apartmentSet.add(String(aptNum));
      }
    });

    // Initialize matrix for all apartments
    Array.from(apartmentSet).forEach(aptNum => {
      matrix[aptNum] = {};
      // Initialize all months as unpaid
      for (let month = 1; month <= 12; month++) {
        matrix[aptNum][month] = false;
      }
    });

    // Mark paid months based on payments
    paymentList.forEach(payment => {
      console.log('Processing payment:', payment);
      
      // Try multiple date fields
      let paymentDate = null;
      const dateFields = [
        payment.date,
        payment.Date, 
        payment.paymentDate,
        payment.PaymentDate,
        payment.paymentDateTime,
        payment.PaymentDateTime,
        payment.createdAt,
        payment.CreatedAt
      ];
      
      for (const dateField of dateFields) {
        if (dateField) {
          const testDate = new Date(dateField);
          if (!isNaN(testDate.getTime())) {
            paymentDate = testDate;
            break;
          }
        }
      }
      
      if (!paymentDate) {
        console.log('Could not parse date for payment:', payment);
        return;
      }
      
      const paymentYear = paymentDate.getFullYear();
      const paymentMonth = payment.month || payment.Month || (paymentDate.getMonth() + 1);

      console.log(`Payment date parsed: Year=${paymentYear}, Month=${paymentMonth}, SelectedYear=${selectedYear}`);

      if (paymentYear === selectedYear) {
        // Get apartment number directly from payment
        const aptNum = String(payment.apartmentNumber || payment.ApartmentNumber);
        
        if (aptNum && matrix[aptNum]) {
          // Check payment type - be more flexible with the check
          const paymentType = payment.paymentType || payment.PaymentType || payment.Type || payment.type || '';
          const paymentTypeLower = paymentType.toLowerCase();
          
          console.log(`Payment type: "${paymentType}" for apartment ${aptNum}, month ${paymentMonth}`);
          
          // Accept various payment type variations
          if (paymentTypeLower.includes('ועד') || 
              paymentTypeLower.includes('vaad') || 
              paymentTypeLower === 'regular' ||
              paymentTypeLower === 'רגיל' ||
              paymentType === '' || // If no type specified, assume it's a valid payment
              !paymentType) { // Also accept if payment type is undefined/null
            matrix[aptNum][paymentMonth] = true;
            console.log(`Marked as paid: Apartment ${aptNum}, Month ${paymentMonth}`);
          }
        }
      }
    });

    console.log('Payment matrix for year', selectedYear, ':', matrix);
    setPaymentMatrix(matrix);
  };

  const getFilteredApartments = () => {
    console.log('getFilteredApartments called, apartments:', apartments);
    if (selectedApartment === 'all') {
      return apartments;
    }
    return apartments.filter(apt => {
      const aptNum = apt.number || apt.apartmentNumber || apt.ApartmentNumber;
      return aptNum === selectedApartment;
    });
  };

  const calculateStats = () => {
    const stats = {
      totalApartments: apartments.length,
      totalMonths: 12,
      totalExpected: apartments.length * 12,
      totalPaid: 0,
      byMonth: {}
    };

    // Initialize month stats
    months.forEach(month => {
      stats.byMonth[month.num] = { paid: 0, total: apartments.length };
    });

    // Count paid payments
    Object.keys(paymentMatrix).forEach(aptNum => {
      months.forEach(month => {
        if (paymentMatrix[aptNum][month.num]) {
          stats.totalPaid++;
          stats.byMonth[month.num].paid++;
        }
      });
    });

    stats.percentage = stats.totalExpected > 0 
      ? Math.round((stats.totalPaid / stats.totalExpected) * 100) 
      : 0;

    return stats;
  };

  const stats = calculateStats();

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>
            <Calendar size={24} />
            סקירת תשלומי ועד בית
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.controls}>
          <div className={styles.controlGroup}>
            <label>שנה:</label>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className={styles.select}
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className={styles.controlGroup}>
            <label>
              <Filter size={16} />
              סינון דירה:
            </label>
            <select 
              value={selectedApartment} 
              onChange={(e) => setSelectedApartment(e.target.value)}
              className={styles.select}
            >
              <option value="all">כל הדירות</option>
              {apartments.map((apt, index) => {
                const aptNum = apt.number || apt.apartmentNumber || apt.ApartmentNumber;
                const aptId = apt.apartmentID || apt.ApartmentID || apt.apartmentId || `select-apt-${index}`;
                return (
                  <option key={aptId} value={aptNum}>
                    דירה {aptNum}
                  </option>
                );
              })}
            </select>
          </div>

          <div className={styles.stats}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>סה"כ דירות:</span>
              <span className={styles.statValue}>{stats.totalApartments}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>תשלומים שבוצעו:</span>
              <span className={styles.statValue}>
                {stats.totalPaid} / {stats.totalExpected}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>אחוז גבייה:</span>
              <span className={styles.statValue}>{stats.percentage}%</span>
            </div>
          </div>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>טוען נתונים...</div>
          ) : apartments.length === 0 ? (
            <div className={styles.loading}>
              לא נמצאו דירות לבניין זה
              <br />
              <small>Building ID: {buildingId}</small>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.apartmentHeader}>
                      <Building size={16} />
                      דירה
                    </th>
                    {months.map(month => {
                      const isCurrentMonth = selectedYear === currentYear && month.num === currentMonth;
                      return (
                        <th key={month.num} className={`${styles.monthHeader} ${isCurrentMonth ? styles.currentMonth : ''}`}>
                          <div className={styles.monthName}>{month.name}</div>
                          <div className={styles.monthStats}>
                            {stats.byMonth[month.num].paid}/{stats.byMonth[month.num].total}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {getFilteredApartments().map((apartment, index) => {
                    const aptNum = apartment.number || apartment.apartmentNumber || apartment.ApartmentNumber;
                    const aptId = apartment.apartmentID || apartment.ApartmentID || apartment.apartmentId || `apt-${index}`;
                    return (
                      <tr key={aptId}>
                        <td className={styles.apartmentCell}>
                          <div className={styles.apartmentInfo}>
                            <span className={styles.apartmentNumber}>{aptNum}</span>
                            {apartment.tenantName && (
                              <span className={styles.tenantName}>{apartment.tenantName}</span>
                            )}
                          </div>
                        </td>
                        {months.map(month => {
                          const isPaid = paymentMatrix[aptNum]?.[month.num] || false;
                          const isCurrentMonth = selectedYear === currentYear && month.num === currentMonth;
                          if (aptNum === '1' && month.num === 1) {
                            console.log('Debug payment status for apt 1, month 1:', {
                              aptNum, 
                              monthNum: month.num,
                              paymentMatrix: paymentMatrix[aptNum],
                              isPaid
                            });
                          }
                          return (
                            <td 
                              key={`${aptNum}-${month.num}`} 
                              className={`${styles.statusCell} ${isPaid ? styles.paid : styles.unpaid} ${isCurrentMonth ? styles.currentMonth : ''}`}
                            >
                              {isPaid ? (
                                <Check size={20} className={styles.checkIcon} />
                              ) : (
                                <X size={20} className={styles.xIcon} />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <Check size={16} className={styles.legendPaid} />
              <span>שולם</span>
            </div>
            <div className={styles.legendItem}>
              <X size={16} className={styles.legendUnpaid} />
              <span>לא שולם</span>
            </div>
          </div>
          <button className={styles.closeButtonFooter} onClick={onClose}>
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusOverview;